import { Component, ErrorInfo, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { Box, Button, Typography, Container } from '@mui/material';
import { MotionContainer, varBounceIn } from '@components/animate';
import HelmetContainer from '@components/HelmetContainer';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

interface FallbackProps {
  error?: string;
  errorInfo?: string;
  resetError: () => void;
}

const RootStyle = styled(HelmetContainer)(({ theme }) => ({
  display: 'flex',
  minHeight: '100%',
  alignItems: 'center',
  paddingTop: theme.spacing(15),
  paddingBottom: theme.spacing(10)
}));

function Fallback({ error, errorInfo, resetError }: FallbackProps) {
  const isDev = process.env.NODE_ENV === 'development';

  return (
    <RootStyle title="500 Page Server Error | Workers high">
      <Container>
        <MotionContainer initial="initial" open>
          <Box sx={{ maxWidth: 480, margin: 'auto', textAlign: 'center' }}>
            <motion.div variants={varBounceIn}>
              <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', mb: 2 }}>
                Oops, Something went wrong
              </Typography>
            </motion.div>
            <Typography sx={{ color: 'text.secondary' }}>
              Sorry, an error has occurred on the server.
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              Please contact the administrator
            </Typography>

            {isDev && error && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: 'error.lighter',
                  borderRadius: 1,
                  textAlign: 'left'
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: 'error.main', mb: 1 }}
                >
                  Error Details (dev only):
                </Typography>
                <Typography
                  component="pre"
                  sx={{
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: 'error.dark',
                    m: 0
                  }}
                >
                  {error}
                </Typography>
                {errorInfo && (
                  <Typography
                    component="pre"
                    sx={{
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: 'text.secondary',
                      mt: 1
                    }}
                  >
                    {errorInfo}
                  </Typography>
                )}
              </Box>
            )}

            <motion.div variants={varBounceIn}>
              <Box
                component="img"
                src="/illustrations/illustration_500.svg"
                sx={{ height: 360, mx: 'auto', my: { xs: 5, sm: 10 } }}
              />
            </motion.div>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                size="large"
                variant="outlined"
                onClick={resetError}
                sx={{
                  borderRadius: 100,
                  height: '48px',
                  fontWeight: 700
                }}
              >
                Try Again
              </Button>
              <Button
                href="/"
                size="large"
                variant="contained"
                component="a"
                sx={{
                  borderRadius: 100,
                  height: '48px',
                  fontWeight: 700
                }}
              >
                Go to Home
              </Button>
            </Box>
          </Box>
        </MotionContainer>
      </Container>
    </RootStyle>
  );
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, errorInfo: null };
    this.resetError = this.resetError.bind(this);
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });

    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorBoundary]', error, errorInfo.componentStack);
    }
  }

  resetError() {
    this.setState({ error: null, errorInfo: null });
  }

  render() {
    const { error, errorInfo } = this.state;
    const { children } = this.props;

    if (errorInfo) {
      return (
        <Fallback
          error={error?.toString()}
          errorInfo={errorInfo.componentStack ?? undefined}
          resetError={this.resetError}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary;
